﻿import React, { useState, useEffect } from 'react';
import { MeasuredDatum } from '../models/measured-datum';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Selection,
    Editing,
    ColumnFixing,
    Toolbar,
    Popup,
    Form,
    Item as ToolBarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Button } from 'devextreme-react/button';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Item } from 'devextreme-react/form';

const _MEASURED_DATA = "measuredData";

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

export interface Props {
    benchSheetId: string;
    spotCheckBenchSheetId?: string;
    isReadOnly: boolean;
    datagridHeight?: string;
    title?: string;
    showSipperMeasuredValue?: boolean;
    readOnly?: boolean;
}

export default function MeasuredData(props: Props) {
    const [canEdit, setCanEdit] =
        useState<boolean>(true);

    const [measuredData, setMeasuredData] =
        useState<MeasuredDatum[]>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [selectedRowsMap, setSelectedRowsMap] =
        useState<Map<string, any[]>>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        //setAuthorizations();
        getMeasuredData();
    }, []);

    return (
        <div>
            <div id="analysis-data-datagrid-container" className="pw-datagrid">
                <DataGrid
                    height={props.datagridHeight ? props.datagridHeight : "100%"}
                    keyExpr="measuredDataId"
                    dataSource={measuredData ? measuredData : []}
                    onRowValidating={props.isReadOnly ? undefined : measuredDataValidating}
                    onSelectionChanged={onSelectionChanged}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true} >

                    <LoadPanel enabled={true} />
                    <ColumnFixing enabled={true} />
                    <Selection mode={props.isReadOnly ? 'none' : 'multiple'} />

                    <Column width={'5em'} caption="" cellRender={measuredDatumInfoButtonTemplate} />

                    <Column dataField={'sampleId'} dataType="string"
                        alignment='center' width={'12%'} />
                    <Column dataField={'catalogNumber'} dataType="string" width={'43%'} />
                    <Column dataField={'lotNumber'} dataType="string" caption="Batch/Lot" width={'15%'} />

                    {!props.showSipperMeasuredValue &&
                        <Column dataField='measuredValue' dataType="number" format={decimalFormat} width={'15%'} />
                    }

                    {props.showSipperMeasuredValue &&
                        <Column dataField='measuredValue' caption="Manual Value" dataType="number" format={decimalFormat} width={'12%'} />
                    }

                    {props.showSipperMeasuredValue &&
                        <Column dataField='sipperMeasuredValue' caption="Sipper Value" dataType="number" format={decimalFormat} width={'12%'} />
                    }

                    <Column dataField={'uom'} width={'15%'} />

                    {!props.isReadOnly &&
                        <Editing mode="popup"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={!props.readOnly}
                            allowDeleting={true}>

                            <Popup title="Edit Measured Data" showTitle={true} width={700} height={380} />
                            <Form>
                                <Item itemType="group" colCount={2} colSpan={2}>
                                    <Item dataField="sampleId" colSpan={2} />
                                    <Item dataField="catalogNumber" />
                                    <Item dataField="lotNumber" />
                                    <Item dataField="measuredValue" />
                                    {props.showSipperMeasuredValue &&
                                        <Item dataField="sipperMeasuredValue" />    
                                    }
                                    <Item dataField="uom" />
                                </Item>
                            </Form>

                        </Editing>
                    }

                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">{props.title ? props.title : "Measured Data"}</div>
                            </div>
                        </ToolBarItem>
                        
                        {!props.isReadOnly &&
                            <ToolBarItem location='after'>
                                <Button width='10rem' type='success' text='Create Measured Data'
                                    disabled={props.readOnly}
                                    onClick={createMeasuredData} />
                            </ToolBarItem>
                        }

                        {!props.isReadOnly &&
                            <ToolBarItem location='after'>
                                <Button width='1.9rem' type='normal' icon="repeat"
                                    hint="duplicate rows" disabled={props.readOnly}
                                    onClick={duplicateRows} />
                            </ToolBarItem>
                        }

                        <ToolBarItem name="addRowButton" location='after' />

                        {/*
                        {!props.isReadOnly &&
                            <ToolBarItem location='after'>
                                <Button width='1.9rem' type='normal' icon="plus"
                                    hint="add analysis data row" disabled={props.readOnly}
                                    onClick={onAddMeasuredDataRowClick} />
                            </ToolBarItem>
                        }
                        */}
                    </Toolbar>

                </DataGrid>

                {/*
                <DialogPopup visible={rawMaterialSearchDialogVisible}
                    onHiding={hideRawMaterialSearchDialog}
                    title="Raw Material Search"
                    width={'55em'}
                    height={'40em'}>
                    <RawMaterialSearch
                        onCancel={hideRawMaterialSearchDialog}
                        onApplySelection={onApplyRawMaterialSelection} />
                </DialogPopup>
                */}


                {itemBasicInformationDialogVisible &&
                    <DialogPopup visible={itemBasicInformationDialogVisible}
                        onHiding={hideItemBasicInformationDialog}
                        title="Item Basic Information"
                        width={'25rem'}
                        height={'21rem'}
                        dragEnabled={false}
                        resizeEnabled={false}>
                        <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                            onOkClick={hideItemBasicInformationDialog} />
                    </DialogPopup>
                }
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function createMeasuredData(e: any) {

        //if (props.onCreateMeasuredData)
          //  props.onCreateMeasuredData();
    }

    function onRefreshMeasuredData() {
        getMeasuredData();

        //if (props.onRefreshMeasuredData)
          //  props.onRefreshMeasuredData();
    }

    /*
    async function addMeasuredDatum(measuredDatum: MeasuredDatum) {
        if (measuredDatum) {
            measuredDatum.benchSheetId = props.benchSheetId;

            await insertMeasuredDatum(measuredDatum)
                .then(() => {
                    getMeasuredData();
                })
                .catch((response: ResponseError) => {
                    XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: response });
                });
        }
    }
    */

    async function measuredDataValidating(e: any) {
        if (props.benchSheetId) {
            //update

            console.log("IN measuredDataValidating");
            console.log(e);


            if (e.oldData && Object.keys(e.oldData).length > 0) {
                let measuredDatum = e.oldData as MeasuredDatum;

                console.log("about to update meas datum");

                if (measuredDatum) {
                    let updatedMeasuredDatum = { ...measuredDatum, ...e.newData };
                    await updateMeasuredDatum(updatedMeasuredDatum);
                }
            }
            else { //insert
                if (e.newData) {
                    let measuredDatum = e.newData as MeasuredDatum;

                    if (measuredDatum) {
                        measuredDatum.benchSheetId = props.spotCheckBenchSheetId ? props.spotCheckBenchSheetId : props.benchSheetId;

                        console.log("about to insert Measured datum");
                        
                        await insertMeasuredDatum(measuredDatum)
                            .then(data => {

                                /*
                                let returnedAnalysisDatum = data as AnalysisDatum;

                                if (returnedAnalysisDatum) {
                                    e.newData.analysisDatumId = returnedAnalysisDatum.analysisDatumId;
                                }
                                */

                            })
                            .catch((res) => {
                                showMessage(res, ModalMessageType.Error, "Error Inserting Measured Data");
                            });
                    }
                }
            }
        }
    }





    /*
    function onAddAnalysisDataRowClick(e: any) {
        showRawMaterialSearchDialog();
    }
     
    function onApplyRawMaterialSelection(e: any) {
        if (e && e.rawMaterialId) {
            let rawMaterial = e as RawMaterial;
    
            if (rawMaterial)
                this.addAnalysisDataRow(rawMaterial);
        }
    }
     
    async function addMeasuredDataRow(rawMaterial: RawMaterial) {
        if (rawMaterial && rawMaterial.rawMaterialId) {
            let measuredDatum: MeasuredDatum = {
                //elution: 0,
                //rawMaterialId: rawMaterial.rawMaterialId
            };
    
            await this.insertMeasuredDataRow(measuredDatum)
                .then(() => {
                    this.hideRawMaterialSearchDialog();
                    this.onRefreshMeasuredData();
                });
        }
    }
    */

    function duplicateRows(e: any) {
        if (selectedRowsMap)
            duplicateMeasuredDataRows(selectedRowsMap.get(_MEASURED_DATA));
    }

    function duplicateMeasuredDataRows(newMeasuredData?: any[]) {
        if (newMeasuredData) {
            let updatedMeasuredData: MeasuredDatum[] = [];

            newMeasuredData.forEach(d => {
                const measuredDatum = d as MeasuredDatum;

                if (measuredDatum)
                    updatedMeasuredData.push(measuredDatum);
            });

            if (updatedMeasuredData.length > 0)
                this.insertMeasuredDataDuplicateRows(updatedMeasuredData)
                    .then(() => {

                        /* HMMMMMM
                        if (this.analysisDataDataGridRef && this.analysisDataDataGridRef.current
                            && this.analysisDataDataGridRef.current.instance)
                            this.analysisDataDataGridRef.current.instance.clearSelection();
                            */

                        onRefreshMeasuredData();
                    });
        }
    }

    
    async function insertMeasuredDataRow(measuredDatum: MeasuredDatum) {
        await insertMeasuredDatum(measuredDatum)
            .then(() => {
                getMeasuredData();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Inserting Measued Data");
            });

        //if (measuredDatum && props.onAddMeasuredDatum)
          //  await props.onAddMeasuredDatum(measuredDatum);
    }

    async function onMeasuredDataRemoving(e: any) {

        console.log("onMeasuredDataRemoving");
        console.log(e);


        // deleteMeasuredData();

        /*
        if (e.data && e.data.MeasuredDataId) {
    
            const match = props.measuredData && props.measuredData.find(a => {
                return a.rawMaterialId === e.data.rawMaterialId && a.measuredDataId !== e.data.measuredDataId
            })
    
            this.setState({ measuredDataIdToDelete: e.data.measuredDataId });
    
            if (match)
                modalConfirmed();
            else {
                XXX.publish(ModalSubscriberTopic,
                    {
                        modalType: ConfirmationModal,
                        message: "Deleting this row will result in a missing raw material. Are you sure you want to delete it?",
                        messageTitle: "Delete Confirmation",
                        onModalConfirmed: this.modalConfirmed
                    });
    
                e.cancel = true;
            }
        }
        */

    }

   

    function onSelectionChanged({ selectedRowKeys, selectedRowsData }: any) {
        if (selectedRowsMap) {
            let map = selectedRowsMap;
            map.set(_MEASURED_DATA, selectedRowsData);

            setSelectedRowsMap(map);
        }
    }

    async function getMeasuredData() {
        const url = props.spotCheckBenchSheetId ? `${props.benchSheetId}/${props.spotCheckBenchSheetId}` : props.benchSheetId;

        await CrudFunctions.getData(`benchsheets/measureddatabybenchsheetid/${url}`)
            .then((data) => {
                setMeasuredData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Measured Data");
            });
    }

    async function insertMeasuredDatum(measuredDatum: MeasuredDatum) {
        return await CrudFunctions.postData("benchsheets/measureddatum", measuredDatum);
    }

    // one of these is not right:
    async function insertMeasuredData() {
        return await CrudFunctions.postData(`benchsheets/createanalysisdataforbenchsheet/${props.benchSheetId}`, null);
    }

    async function insertMeasuredDataDuplicateRows(measuredData: MeasuredDatum[]) {
        if (measuredData) {
            await CrudFunctions.postData("benchsheets/measureddatarows", measuredData)
                .then(() => {
                    getMeasuredData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Inserting Measured Data");
                });
        }
    }

    async function updateMeasuredDatum(measuredDatum: MeasuredDatum) {
        return await CrudFunctions.putData("benchsheets/measureddatum", measuredDatum);
    }

    async function deleteMeasuredData(measuredDataId: string) {
        return await CrudFunctions.deleteData(`benchsheets/measureddatum/${measuredDataId}`);
    }

    function measuredDatumInfoButtonTemplate(e: any) {
        return (
            <div className="bs-icon-group-together">
                <Button
                    className="bs-icon-custom-edit"
                    icon="info"
                    onClick={() => onMeasuredDatumInfoButtonClick(e)}>
                </Button>
                <Button
                    className="bs-icon-custom-edit"
                    icon="trash"
                    onClick={() => onMeasuredDataRemoving(e)} >
                </Button>
            </div>
        );
    }

    // show/hide

    function onMeasuredDatumInfoButtonClick(e: any) {
        if (measuredData) {
            let entry = measuredData.find(a => a.measuredDataId === e.key)
            if (entry) {
                let itemBasicInitInformation: ItemBasicInformation = {
                    rowPointer: entry.measuredDataId,
                    createTimestamp: entry.createTimestamp,
                    lastModifiedTimestamp: entry.modifyTimestamp,
                    createUser: entry.createUser,
                    modifyUser: entry.modifyUser
                };

                setItemBasicInitInformation(itemBasicInitInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }


    /*
    function showRawMaterialSearchDialog() {
        this.setState({ rawMaterialSearchDialogVisible: true });
    }

    function hideRawMaterialSearchDialog() {
        this.setState({ rawMaterialSearchDialogVisible: false });
    }
        
    //render

    function componentActualRawMaterialRender(e: any) {
        if (e.data && e.data.rawMaterialName) {
            return (
                <div>
                    <div className="item-main">{e.data.rawMaterialName}</div>
                    <div className="item-sub">{e.data.rawMaterialDescription}</div>
                </div>
            );
        }
        else
            return (<div></div>);
    }


    
    //other

    modalConfirmed() {
        if (this.state.analysisDataIdToDelete)
            this.deleteAnalysisData(this.state.analysisDataIdToDelete)
                .then(() => {
                    //remove from datagrid
                    this.onRefreshAnalysisData();
                })
                .catch(response => {
                    XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: response });
                });
    }
    */



}





